import { FaceIcon, GitHubLogoIcon, StarIcon } from '@radix-ui/react-icons';
import { Badge, Box, Card, Code, Flex, Heading, Inset, Text } from '@workos-inc/design-system';
import { mdxPlugins, remarkScreenshots, slugToPath } from '@workos-inc/docs-kit';
import fs from 'fs';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { MDXRemote, MDXRemoteProps } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import { emojify } from 'node-emoji';
import * as React from 'react';
import { Breadcrumb } from 'src/components/breadcrumb';
import { Header } from 'src/components/header';
import { mdxComponents, releaseMdxComponents } from 'src/components/mdx-components';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { Timeline } from 'src/components/timeline';
import { CodeForkIcon } from 'src/icons/code-fork-icon';
import { JavascriptLogoIcon } from 'src/icons/javascript-logo-icon';
import { NextJsLogoIcon } from 'src/icons/nextjs-logo-icon';
import { ReactLogoIcon } from 'src/icons/react-logo-icon';
import { RemixLogoIcon } from 'src/icons/remix-logo-icon';
import { TanStackLogoIcon } from 'src/icons/tanstack-logo-icon';
import { AUTHKIT_SDK_META, SDK_META } from 'src/utils/constants';
import { convertBulletToMarkdownList } from 'src/utils/convert-bullets';
import { createPageImage, createPageTitle } from 'src/utils/create-page-metadata';
import { formatDate } from 'src/utils/format-date';
import { makeOctokit } from 'src/utils/octokit';
import { rehypeCodeBlockSnippets } from 'src/utils/rehype-code-block-snippets';
import { remarkPrLinks } from 'src/utils/remark-pr-links';
interface Release {
  content: string;
  title: string;
  latest: boolean;
  prerelease: boolean;
  publishedAt: string;
}
type SdkMetaItem = (typeof SDK_META)[number] | (typeof AUTHKIT_SDK_META)[number];
interface SdkPageProps {
  content: string;
  sdk: SdkMetaItem;
  releases: Release[];
  pageMeta: {
    title: string;
    canonicalUrl: string;
    description: string;
    image: string;
  };
  stats: {
    stargazers: number;
    contributors: number;
    forks: number;
  };
  isAuthKit: boolean;
}
export const getStaticPaths: GetStaticPaths = () => ({
  paths: [...SDK_META, ...AUTHKIT_SDK_META].map(sdk => ({
    params: {
      slug: sdk.key
    }
  })),
  fallback: false
});
export const getStaticProps: GetStaticProps = async context => {
  if (typeof context.params?.slug !== 'string') {
    return {
      notFound: true
    };
  }
  const slug = context.params.slug;

  // Check if it's a backend SDK or AuthKit SDK
  const backendSdk = SDK_META.find(({
    key
  }) => slug === key);
  const authkitSdk = AUTHKIT_SDK_META.find(({
    key
  }) => slug === key);
  const sdk = backendSdk ?? authkitSdk;
  const isAuthKit = !!authkitSdk;
  if (!sdk) {
    return {
      notFound: true
    };
  }

  // Reconstruct the path to the MDX file
  const [filePath] = slugToPath(['sdks', slug]);
  if (filePath === null) {
    return {
      notFound: true
    };
  }
  const rawMdx = await new Promise<Buffer>(resolve => fs.readFile(filePath, (_, data) => resolve(data)));
  const {
    compiledSource: content
  } = await serialize(rawMdx, {
    mdxOptions: {
      remarkPlugins: [...mdxPlugins.remark, remarkScreenshots],
      rehypePlugins: [rehypeCodeBlockSnippets(filePath), ...mdxPlugins.rehype]
    }
  });
  const octokit = makeOctokit();
  const {
    data: releases
  } = await octokit.repos.listReleases({
    owner: 'workos',
    repo: sdk.repo
  });
  const releasesMdx = await Promise.all(releases.map(async release => {
    // Convert GitHub-style emoji shortcodes (e.g., :emoji:) to Unicode characters
    // and convert bullet characters to proper markdown list syntax
    const bodyWithEmoji = convertBulletToMarkdownList(emojify(release.body || ''));

    // Escape curly braces in release body to prevent MDX from interpreting them as JSX expressions
    // This handles cases like {user_id} in API paths which would cause "ReferenceError: user_id is not defined"
    const escapedBody = bodyWithEmoji.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, '\\{$1\\}');
    const {
      compiledSource
    } = await serialize(escapedBody, {
      scope: {},
      mdxOptions: {
        remarkPlugins: [remarkPrLinks]
      }
    });
    return {
      title: release.name || release.tag_name,
      latest: releases.find(release => !release.prerelease)?.tag_name === release.tag_name,
      prerelease: release.prerelease,
      publishedAt: release.published_at,
      content: compiledSource
    };
  }));
  const [forks, contributors, stargazers] = await Promise.all([octokit.repos.listForks({
    owner: 'workos',
    repo: sdk.repo,
    per_page: 1
  }), octokit.repos.listContributors({
    owner: 'workos',
    repo: sdk.repo,
    per_page: 1
  }), octokit.rest.activity.listStargazersForRepo({
    owner: 'workos',
    repo: sdk.repo,
    per_page: 1
  })]);

  /** Look into response headers and count how many pages of data exist */
  const count = ({
    headers
  }: {
    headers: {
      link?: string;
    };
  }) => headers.link?.replace(/(.+=)(\d+)(>; +rel="last")/, '$2');
  const title = isAuthKit ? `AuthKit ${sdk.name} SDK` : `${sdk.name} SDK`;
  const description = isAuthKit ? createAuthKitSdkPageDescription(sdk.name) : createSdkPageDescription(sdk.name, 'experimental' in sdk ? sdk.experimental : false);
  const image = await createPageImage(title, description, 'SDKs');
  return {
    props: {
      sdk,
      content,
      releases: releasesMdx,
      isAuthKit,
      pageMeta: {
        title,
        canonicalUrl: `https://workos.com/docs/sdks/${slug}`,
        description,
        image
      },
      stats: {
        stargazers: count(stargazers) ?? 0,
        contributors: count(contributors) ?? 0,
        forks: count(forks) ?? 0
      }
    }
  };
};
export const createSdkPageDescription = (name: string, experimental: boolean) => experimental ? `Experimental ${name} SDK for interacting with the WorkOS API.` : `Official ${name} SDK for interacting with the WorkOS API.`;
export const createAuthKitSdkPageDescription = (name: string) => `Official AuthKit SDK for ${name} applications.`;
const AuthKitHeroIcon = ({
  sdkKey
}: {
  sdkKey: string;
}) => {
  const iconProps = {
    height: '120',
    width: '120'
  };
  switch (sdkKey) {
    case 'authkit-js':
      return <JavascriptLogoIcon {...iconProps} />;
    case 'authkit-react':
    case 'authkit-react-router':
      return <ReactLogoIcon {...iconProps} />;
    case 'authkit-nextjs':
      return <NextJsLogoIcon {...iconProps} />;
    case 'authkit-remix':
      return <RemixLogoIcon {...iconProps} />;
    case 'authkit-tanstack-start':
      return <TanStackLogoIcon {...iconProps} />;
    default:
      return null;
  }
};
const SdkPage = ({
  content,
  releases,
  sdk,
  stats,
  pageMeta,
  isAuthKit
}: SdkPageProps) => {
  const title = createPageTitle(pageMeta.title);
  const {
    description,
    image
  } = pageMeta;
  return <Box data-sentry-element="Box" data-sentry-component="SdkPage" data-sentry-source-file="[slug].tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="[slug].tsx">
        <title>{title}</title>
        <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <link href={pageMeta.canonicalUrl} rel="canonical" />
      </Head>
      <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="[slug].tsx" />
      <Header caption="SDKs" data-sentry-element="Header" data-sentry-source-file="[slug].tsx" />
      <PageLayout.Root layout="centered" data-sentry-element="PageLayout.Root" data-sentry-source-file="[slug].tsx">
        <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="[slug].tsx">
          <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="[slug].tsx" />
        </PageLayout.Nav>

        <PageLayout.Content data-sentry-element="PageLayout.Content" data-sentry-source-file="[slug].tsx">
          <Breadcrumb href="/sdks" data-sentry-element="Breadcrumb" data-sentry-source-file="[slug].tsx">SDKs</Breadcrumb>

          <Box mb="7" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
            <Heading as="h1" mb="5" size="7" data-sentry-element="Heading" data-sentry-source-file="[slug].tsx">
              {isAuthKit ? `AuthKit ${sdk.name} SDK` : `${sdk.name} SDK`}
            </Heading>

            <Card asChild size="3" data-sentry-element="Card" data-sentry-source-file="[slug].tsx">
              <a href={`https://github.com/workos/${sdk.repo}`} rel="noreferrer" target="_blank">
                <Inset clip="padding-box" data-sentry-element="Inset" data-sentry-source-file="[slug].tsx">
                  <Flex data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                    <Flex className={isAuthKit ? undefined : 'light-theme'} direction="column" flexGrow="1" height="360px" justify="end" p="4" position="relative" style={{
                    color: 'var(--gray-12)',
                    backgroundColor: 'var(--color-background)',
                    ...(isAuthKit ? {} : {
                      backgroundImage: `url(https://images.workoscdn.com/docs/sdks/hero-${sdk.key}.jpg)`,
                      backgroundSize: 'auto 400px',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }),
                    overflow: 'hidden'
                  }} data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                      {isAuthKit && <Flex align="center" justify="center" position="absolute" style={{
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: '80px',
                      color: 'var(--gray-11)'
                    }}>
                          <AuthKitHeroIcon sdkKey={sdk.key} />
                        </Flex>}
                      <Box mb="3" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                        <Box display={{
                        sm: 'none'
                      }} data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                          <Text color="gray" size="2" data-sentry-element="Text" data-sentry-source-file="[slug].tsx">
                            github.com
                          </Text>
                        </Box>
                        <Text size="6" data-sentry-element="Text" data-sentry-source-file="[slug].tsx">workos/</Text>
                        <wbr />
                        <Text size="6" style={{
                        whiteSpace: 'nowrap'
                      }} weight="bold" data-sentry-element="Text" data-sentry-source-file="[slug].tsx">
                          {sdk.repo}
                        </Text>
                      </Box>

                      <Text color="gray" size="2" style={{
                      whiteSpace: 'nowrap'
                    }} data-sentry-element="Text" data-sentry-source-file="[slug].tsx">
                        <Flex gap="4" data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                          <Flex align="center" gap="1" data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                            <Box flexShrink="0" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                              <StarIcon data-sentry-element="StarIcon" data-sentry-source-file="[slug].tsx" />
                            </Box>
                            {stats.stargazers} Stars
                          </Flex>

                          <Flex align="center" gap="1" data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                            <Box flexShrink="0" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                              <CodeForkIcon data-sentry-element="CodeForkIcon" data-sentry-source-file="[slug].tsx" />
                            </Box>
                            {stats.forks} Forks
                          </Flex>

                          <Flex align="center" gap="1" minWidth="0" data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                            <Box flexShrink="0" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                              <FaceIcon data-sentry-element="FaceIcon" data-sentry-source-file="[slug].tsx" />
                            </Box>
                            <Box style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden'
                          }} data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                              {stats.contributors} Contributors
                            </Box>
                          </Flex>
                        </Flex>
                      </Text>
                    </Flex>

                    <Flex align="center" className="dark-theme" display={{
                    initial: 'none',
                    sm: 'flex'
                  }} flexGrow="0" flexShrink="1" style={{
                    color: 'var(--gray-12)',
                    background: 'var(--color-background)'
                  }} data-sentry-element="Flex" data-sentry-source-file="[slug].tsx">
                      <Box maxWidth="260px" p="6" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                        <Box mb="4" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                          <GitHubLogoIcon height="40" width="40" data-sentry-element="GitHubLogoIcon" data-sentry-source-file="[slug].tsx" />
                        </Box>
                        <Text as="div" color="gray" mb="1" size="2" data-sentry-element="Text" data-sentry-source-file="[slug].tsx">
                          github.com
                        </Text>
                        <Text size="5" data-sentry-element="Text" data-sentry-source-file="[slug].tsx">{description}</Text>
                      </Box>
                    </Flex>
                  </Flex>
                </Inset>
              </a>
            </Card>
          </Box>

          <Box mb="7" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
            <MDXRemote compiledSource={content}
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          components={mdxComponents as MDXRemoteProps['components']} frontmatter={mdxRemoteFrontmatter} scope={mdxRemoteScope} data-sentry-element="MDXRemote" data-sentry-source-file="[slug].tsx" />
            <Heading as="h3" mb="1" size="3" data-sentry-element="Heading" data-sentry-source-file="[slug].tsx">
              Beta Versions
            </Heading>
            <Text data-sentry-element="Text" data-sentry-source-file="[slug].tsx">
              Certain WorkOS features may be available only in the beta version
              of the SDK. Beta versions have the{' '}
              <Code color="gray" data-sentry-element="Code" data-sentry-source-file="[slug].tsx">-beta.*</Code> suffix, for example,{' '}
              <Code color="gray" data-sentry-element="Code" data-sentry-source-file="[slug].tsx">3.2.0-beta.1</Code>. For more information on
              how to use the beta versions, refer to the README in the GitHub
              repository.
            </Text>
          </Box>

          <Box mb="7" data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
            <Heading as="h2" mb="4" size="5" data-sentry-element="Heading" data-sentry-source-file="[slug].tsx">
              Releases
            </Heading>

            <Timeline.Root top={16} data-sentry-element="Timeline.Root" data-sentry-source-file="[slug].tsx">
              <Timeline.Track data-sentry-element="Timeline.Track" data-sentry-source-file="[slug].tsx" />

              <Box data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                {releases.map(({
                content,
                title,
                latest,
                prerelease,
                publishedAt
              }) => <React.Fragment key={title}>
                      <Box mb="8">
                        <Flex mb="4">
                          <Flex align="center" height="28px">
                            <Timeline.Point latest={latest}>
                              {formatDate(publishedAt, 'MMMM d, Y')}
                            </Timeline.Point>
                          </Flex>

                          <Flex align={{
                      initial: 'start',
                      sm: 'center'
                    }} direction={{
                      initial: 'column',
                      sm: 'row'
                    }} gap="2">
                            <Flex gap="2" ml="-2px" wrap="wrap">
                              <h3 className="rt-reset">
                                <Badge color="gray" size="3">
                                  {title}
                                </Badge>
                              </h3>

                              {prerelease && <Badge color="yellow" size="3">
                                  Pre-release
                                </Badge>}

                              {latest && <Badge color="green" size="3">
                                  Latest
                                </Badge>}
                            </Flex>

                            <Box display={{
                        md: 'none'
                      }}>
                              <Text color="gray" size="2" wrap="nowrap">
                                {formatDate(publishedAt, 'MMMM d, Y')}
                              </Text>
                            </Box>
                          </Flex>
                        </Flex>

                        <MDXRemote compiledSource={content} frontmatter={mdxRemoteFrontmatter} scope={mdxRemoteScope} components={
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  releaseMdxComponents as MDXRemoteProps['components']} />
                      </Box>
                    </React.Fragment>)}
              </Box>
            </Timeline.Root>
          </Box>
        </PageLayout.Content>
      </PageLayout.Root>
    </Box>;
};

// Don't pass empty object to MDX Remote directly.
// MDX Remote must receive a stable object, otherwise performance on large pages suffers.
const mdxRemoteFrontmatter = {};
const mdxRemoteScope = {};
export default SdkPage;